import { useEffect, useState } from 'react';

export default function Hero() {
  const [timeLeft, setTimeLeft] = useState({ hours: 5, minutes: 47, seconds: 23 });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        let { hours, minutes, seconds } = prev;
        seconds -= 1;
        if (seconds < 0) { seconds = 59; minutes -= 1; }
        if (minutes < 0) { minutes = 59; hours -= 1; }
        if (hours < 0) { hours = 23; minutes = 59; seconds = 59; }
        return { hours, minutes, seconds };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-[#0a0a0a] pt-24 pb-16 px-4"
    >
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] md:w-[700px] md:h-[700px] rounded-full bg-[#8b5cf6]/10 blur-[100px]" />
        <div className="absolute top-1/4 right-1/4 w-[200px] h-[200px] rounded-full bg-[#ec4899]/8 blur-[80px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[180px] h-[180px] rounded-full bg-[#10b981]/8 blur-[80px]" />
      </div>

      <div className="relative z-10 w-full max-w-4xl mx-auto flex flex-col items-center text-center gap-6 md:gap-8">

        {/* Urgency Badge */}
        <div className="inline-flex items-center gap-2 bg-[#8b5cf6]/10 border border-[#8b5cf6]/50 text-[#c4b5fd] text-xs md:text-sm font-semibold px-4 py-2 rounded-full animate-pulse">
          🔥 OFERTA LIMITADA — SOLO HOY
        </div>

        {/* Countdown */}
        <div className="flex items-center gap-2 md:gap-3">
          {[
            { label: 'Horas', value: pad(timeLeft.hours) },
            { label: 'Min', value: pad(timeLeft.minutes) },
            { label: 'Seg', value: pad(timeLeft.seconds) },
          ].map((item, i) => (
            <div key={i} className="flex flex-col items-center">
              <div className="bg-[#1f2937] border border-[#8b5cf6]/40 rounded-xl px-3 py-1.5 md:px-4 md:py-2 min-w-[48px] md:min-w-[56px] text-center">
                <span className="text-xl md:text-3xl font-black text-white font-mono">{item.value}</span>
              </div>
              <span className="text-[9px] md:text-[10px] text-gray-500 mt-1 uppercase tracking-widest">{item.label}</span>
            </div>
          ))}
        </div>

        {/* H1 */}
        <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-black leading-tight tracking-tight">
          <span className="text-white">¡Deja de pagar</span>
          <br />
          <span className="bg-gradient-to-r from-[#8b5cf6] via-[#ec4899] to-[#10b981] bg-clip-text text-transparent">
            mensualidades!
          </span>
          <br />
          <span className="text-white text-2xl sm:text-3xl md:text-5xl lg:text-6xl">
            Multiplica tus ingresos con el
          </span>
          <br />
          <span className="text-white">Ecosistema Digital</span>{' '}
          <span className="bg-gradient-to-r from-[#10b981] to-[#8b5cf6] bg-clip-text text-transparent">
            Definitivo.
          </span>
        </h1>

        {/* Subtitle */}
        <p className="text-base md:text-xl text-gray-300 max-w-2xl leading-relaxed">
          Acceso total a las mejores herramientas de IA y edición por un año.
          <br />
          <span className="text-gray-400 text-sm md:text-base">💻 Laptop &nbsp;🖥️ PC &nbsp;📱 Celular &nbsp;📟 Tablet</span>
          <br />
          <span className="text-[#10b981] font-medium text-sm md:text-base">Compatible con cualquier dispositivo.</span>
        </p>

        {/* Trust Block */}
        <div className="w-full max-w-xl bg-[#0f1a14]/80 border border-[#10b981]/40 rounded-2xl px-4 md:px-6 py-4 md:py-5 text-left space-y-2">
          <p className="text-[#10b981] font-bold text-xs md:text-sm uppercase tracking-wider mb-2 md:mb-3">Garantía de Confianza</p>
          {[
            '✅ Cuentas 100% Oficiales.',
            '✅ NO son archivos raros ni APKs que dañan tu equipo.',
            '✅ Resultados de agencia garantizados.',
          ].map((item, i) => (
            <p key={i} className="text-gray-200 text-sm font-medium">{item}</p>
          ))}
        </div>

        {/* CTA Button — split into 2 lines on mobile */}
        <a
          href="https://wa.link/3odpj7"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-3 bg-gradient-to-r from-[#8b5cf6] to-[#ec4899] text-white font-black text-base md:text-xl px-6 md:px-10 py-4 md:py-5 rounded-full cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(139,92,246,0.6)] shadow-[0_0_25px_rgba(139,92,246,0.4)] w-full max-w-lg text-center leading-snug"
          style={{ animation: 'ctaPulse 2s ease-in-out infinite' }}
        >
          <span className="text-lg">⚡</span>
          <span>
            ¡QUIERO MI ACCESO
            <br className="sm:hidden" />
            {' '}TOTAL AHORA!
          </span>
          <span className="hidden sm:inline group-hover:translate-x-1 transition-transform duration-200">→</span>
        </a>

        {/* Social proof */}
        <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 text-gray-500 text-xs md:text-sm">
          <div className="flex -space-x-2">
            {[
              'https://readdy.ai/api/search-image?query=young%20latin%20professional%20smiling%20portrait%20closeup%20neutral%20background&width=40&height=40&seq=1&orientation=squarish',
              'https://readdy.ai/api/search-image?query=young%20woman%20entrepreneur%20smiling%20portrait%20closeup%20neutral%20background&width=40&height=40&seq=2&orientation=squarish',
              'https://readdy.ai/api/search-image?query=young%20man%20digital%20creator%20smiling%20portrait%20closeup%20neutral%20background&width=40&height=40&seq=3&orientation=squarish',
            ].map((src, i) => (
              <div key={i} className="w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-[#0a0a0a] overflow-hidden">
                <img src={src} alt="usuario" className="w-full h-full object-cover object-top" />
              </div>
            ))}
          </div>
          <span>+2,400 personas ya tienen su acceso</span>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-[#8b5cf6]/60" style={{ animation: 'bounce 2s infinite' }}>
        <i className="ri-arrow-down-line text-xl md:text-2xl"></i>
      </div>

      <style>{`
        @keyframes ctaPulse {
          0%, 100% { box-shadow: 0 0 25px rgba(139,92,246,0.4); }
          50% { box-shadow: 0 0 50px rgba(139,92,246,0.7), 0 0 80px rgba(236,72,153,0.3); }
        }
      `}</style>
    </section>
  );
}
